import type { SetPriorityAction as SetPriorityActionType } from "@redotech/redo-flows-common/rules/schemas";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";

type PriorityOption = "URGENT" | "NONE";

const priorityOptions = (): RedoListItem<PriorityOption>[] => [
  { id: "URGENT", type: "text", text: t("Urgent"), value: "URGENT" },
  { id: "NONE", type: "text", text: t("None (clear)"), value: "NONE" },
];

export const SetPriorityAction = memo(function SetPriorityAction({
  action,
  onChange,
  editableFields,
}: {
  action: SetPriorityActionType;
  onChange: (updated: SetPriorityActionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const priorityEditable =
    editableFields === undefined || editableFields.has("priority");

  const selected = useMemo(() => {
    const value: PriorityOption =
      action.priority === "URGENT" ? "URGENT" : "NONE";
    return priorityOptions().find((opt) => opt.value === value);
  }, [action.priority]);

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput<PriorityOption>
        disabled={!priorityEditable}
        fitToAnchor
        label={t("Priority", { context: "support ticket priority" })}
        options={priorityOptions()}
        optionSelected={({ item }) => {
          if (!priorityEditable) {
            return;
          }
          onChange({
            ...action,
            priority: item.value === "URGENT" ? "URGENT" : null,
          });
        }}
        selectedItem={selected}
      />
    </Flex>
  );
});
